import { Grid, Stack } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { CuriosityValue } from '@procureai/curiosity-ui-mui';
import { useCurrency } from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';

import { NULLISH_VALUE } from '~/common/constants';
import { BaseDetailDomainType, I18nNamespace, PurchaseOrder } from '~/common/types';
import {
  ColumnSelector,
  DataGridProStyled,
  DataGridWidgetHeader,
  DensityButton,
  ResetRemoteStateButton,
  useDataGridRemoteState,
} from '~/components/DataGridCustom';
import { LabeledDataRow } from '~/components/LabeledDataRow/LabeledDataRow';

import { usePurchaseOderDetailsDataGridConfig } from './PurchaseOrderDetails.dataGrid';

const domain = BaseDetailDomainType.PURCHASE_ORDER_DETAILS;

export interface PurchaseOrderDetailsProps {
  data?: PurchaseOrder;
  loading?: boolean;
}

export const PurchaseOrderDetails = ({ data, loading }: PurchaseOrderDetailsProps) => {
  const { format: formatCurrency } = useCurrency();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'PurchaseOrderDetailsWidget' });

  const apiRef = useGridApiRef();
  const config = usePurchaseOderDetailsDataGridConfig();

  const {
    loading: loadingRemoteState,
    gridProps,
    onDensityChange,
    onRemoteStateReset,
  } = useDataGridRemoteState({
    apiRef,
    config,
    domain,
  });

  const items = data?.items;
  const totalValue = formatCurrency(data?.totalValue ?? 0);

  const skip = loadingRemoteState || !items;

  return (
    <>
      <Grid
        container
        direction="row"
        spacing={4}
        sx={{
          justifyContent: 'flex-start',
          alignItems: 'stretch',
          alignContent: 'flex-start',
        }}
      >
        {items && items.length ? (
          <Grid container spacing={4} size={12}>
            <Grid size={12}>
              <LabeledDataRow loading={loading} label={t('externalReference')} body={data.externalReference} />
            </Grid>
            <Grid size={12}>
              <LabeledDataRow
                loading={loading}
                label={t('supplierName')}
                body={<CuriosityValue ns="supplier" i18nKey={data.supplier ?? ''} fallback={NULLISH_VALUE} />}
              />
            </Grid>
            <Grid size={12}>
              <LabeledDataRow loading={loading} label={t('totalValue')} body={totalValue} />
            </Grid>
            <Grid size={12}>
              <LabeledDataRow loading={loading} label={t('description')} body={data.description ?? NULLISH_VALUE} />
            </Grid>

            <Grid size={12}>
              <DataGridWidgetHeader
                disablePadding
                left={t('items')}
                right={
                  <Stack direction="row">
                    <DensityButton density={gridProps.density} onDensityChange={onDensityChange} />
                    <ColumnSelector
                      columns={gridProps.columns}
                      columnVisibilityModel={gridProps.columnVisibilityModel}
                      onColumnVisibilityModelChange={gridProps.onColumnVisibilityModelChange}
                    />
                    <ResetRemoteStateButton onRemoteStateReset={onRemoteStateReset} />
                  </Stack>
                }
              />

              {skip ? null : (
                <DataGridProStyled
                  {...config}
                  apiRef={apiRef}
                  autoHeight
                  hideFooter
                  disableColumnMenu
                  loading={loading}
                  rows={items}
                  rowSelection={false}
                  {...gridProps}
                />
              )}
            </Grid>
          </Grid>
        ) : null}
      </Grid>
    </>
  );
};
